/**
 * Gateway approvals on file — `/v1/admin/gateway-approvals` (11-compliance/02 §4.3). Superadmin
 * only, so the list is fetched only for a superadmin; anybody else would read a 403.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import { putPresigned } from '../storage';
import type { components } from '../../../api/generated/schema';

export type GatewayApprovalRow = components['schemas']['GatewayApprovalRow'];
export type GatewayApprovalIn = components['schemas']['GatewayApprovalIn'];
type GatewayApprovalListResponse = components['schemas']['GatewayApprovalListResponse'];
type LetterUploadOut = components['schemas']['LetterUploadOut'];
type ConfirmOut = components['schemas']['ConfirmOut'];

/** What the `legal/` prefix accepts; the server checks again, this only narrows the picker. */
export const LETTER_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;

export function useGatewayApprovals(enabled: boolean) {
  return useQuery({
    queryKey: keys.gatewayApprovals.list(),
    queryFn: () => authedClient.request<GatewayApprovalListResponse>('/v1/admin/gateway-approvals'),
    enabled,
  });
}

/** mint → PUT straight to storage → confirm, which starts the scan. Returns the letter's id. */
export function useUploadLetter() {
  return useMutation({
    mutationFn: async (file: File) => {
      const minted = await authedClient.request<LetterUploadOut>('/v1/admin/gateway-approvals/letters', {
        method: 'POST',
        body: { content_type: file.type, declared_bytes: file.size },
      });
      await putPresigned(minted.upload, file, 'No approval was recorded.');
      return authedClient.request<ConfirmOut>(
        `/v1/admin/gateway-approvals/letters/${minted.media_object_id}/confirm`,
        { method: 'POST' },
      );
    },
  });
}

/** Filing an approval can unlock a flag, so the flag list is refetched with it. */
export function useRecordApproval() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: GatewayApprovalIn) =>
      authedClient.request<GatewayApprovalRow>('/v1/admin/gateway-approvals', { method: 'POST', body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.gatewayApprovals.all() });
      void client.invalidateQueries({ queryKey: keys.flags.all() });
    },
  });
}
